import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import { Field, Form } from 'react-final-form'
import { Button } from '@material-ui/core'
import { List } from 'immutable'
/* eslint-disable import/named -- fetchProjectedSitesManagerLookupTable709 is exported from reducer.js */
import {
  fetchProjectedSitesManagerDietGroups,
  fetchProjectedSitesManagerLookupTable709,
  fetchProjectedSitesManagerProductFamily,
  fetchProjectedSitesManagerSingleItem,
  saveSingleItemForm,
  setActiveSiteRow,
  setBaseProducts,
  setDietGroups,
  setProjectedProducts,
} from 'reducers/projectedSitesManager/reducer'
/* eslint-enable import/named */
import Input from 'components/common/Form/Input'
import getValidator, { VALIDATOR_IS_EMPTY } from 'validation'
import withPromise from 'helpers/withPromise'
import { Select } from 'components/common/FormInputs'
import {
  FRESHWATER_CLIENT_REASONS,
  SEAWATER_SITE_REASONS,
} from 'constants/projectedSitesManager'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import { SITE_TYPE_SEAWATER } from 'constants/index'
import messages from '../translate'

function EditForm({
  intl: { formatMessage },
  activeSiteRow,
  setActiveSiteRow,
  fetchProjectedSitesManagerProductFamily,
  fetchProjectedSitesManagerDietGroups,
  fetchProjectedSitesManagerLookupTable709,
  saveSingleItemForm,
  fetchProjectedSitesManagerSingleItem,
  onPanelClose,
  storePsfs,
  siteType,
  snapshot,
  siteId,
  setDietGroups,
  productFamilyList,
  dietGroupList,
  singleItemFormLookup,
  specieId,
  year,
  setScrollHeight,
  scrollToCurrentProduct,
}) {
  const {
    userProjectedProductFamilyId,
    id: activeId,
    clientId: activeClientId,
    monthYear: activeMonthYear,
  } = activeSiteRow
  const fullWidth = ifWindowWidthLessThan(599)
  const isSeawater = siteType === SITE_TYPE_SEAWATER
  const itemReasons = isSeawater
    ? SEAWATER_SITE_REASONS
    : FRESHWATER_CLIENT_REASONS
  const lookup = singleItemFormLookup.toJS()
  const pigmentOptions = (lookup.Pigmento || []).map(value => ({
    id: value,
    name: value,
  }))
  const userProjectedPigmentSelectList = [
    { id: '', name: '' },
    ...pigmentOptions,
  ]
  const userProjectedAdditivesSelectList = (
    lookup.Aditivos || []
  ).map(value => ({ id: value, name: value }))
  const histidineOptions = (lookup.Histidina || []).map(value => ({
    id: value,
    name: value,
  }))
  const userProjectedHistidineSelectList = [
    { id: '', name: '' },
    ...histidineOptions,
  ]
  const userProjectedDietGroupList = dietGroupList
    .toJS()
    .map(({ dietGroup }) => ({
      id: dietGroup,
      name: dietGroup,
    }))

  useEffect(() => {
    fetchProjectedSitesManagerProductFamily()
    fetchProjectedSitesManagerLookupTable709()
  }, [])

  useEffect(() => {
    if (userProjectedProductFamilyId) {
      fetchProjectedSitesManagerDietGroups({
        productFamilyId: userProjectedProductFamilyId,
      })
    }
  }, [activeId, activeClientId, activeMonthYear])

  const formatDigitValue = value => {
    if (value === undefined || value === null) return ''

    const lastKey = String(value).slice(-1)
    const oldValue = String(value).slice(0, -1)

    if (/^[0-9]*\.?[0-9]*$/.test(lastKey)) {
      return value
    }

    return oldValue
  }

  const onFieldKeyUp = ({ target: { value } }, fieldName) => {
    setActiveSiteRow({ [fieldName]: value })
  }

  const renderNumberInput = (fieldName, required = true) => (
    <Field
      required={required}
      name={fieldName}
      type="text"
      label={formatMessage(messages[fieldName])}
      component={Input}
      className="form-control"
      onKeyUp={event => onFieldKeyUp(event, fieldName)}
      format={formatDigitValue}
      validate={required && getValidator(VALIDATOR_IS_EMPTY)}
    />
  )

  const convertToNumber = param => (param ? Number(param) : null)

  const onSubmitForm = async ({
    id,
    clientId,
    monthYear,
    userProjectedProductFamilyId,
    userProjectedDietGroup,
    userProjectedFeedConsumption,
    userProjectedSales,
    reason,
    userProjectedAdditives2,
    userProjectedPigment2,
    userProjectedHistidine,
    userProjectedPosition,
  }) => {
    const data = {
      reason,
      monthYear: isSeawater ? undefined : monthYear,
      client: isSeawater ? undefined : clientId,
      userProjectedProductFamily: convertToNumber(userProjectedProductFamilyId),
      userProjectedDietGroup: userProjectedDietGroup || undefined,
      userProjectedAdditives2:
        userProjectedAdditives2 && userProjectedAdditives2.length
          ? userProjectedAdditives2
          : undefined,
      userProjectedPigment2:
        userProjectedPigment2 != null &&
        String(userProjectedPigment2).trim() !== ''
          ? String(userProjectedPigment2).trim()
          : undefined,
      userProjectedHistidine:
        userProjectedHistidine != null &&
        String(userProjectedHistidine).trim() !== ''
          ? String(userProjectedHistidine).trim()
          : undefined,
      userProjectedFeedConsumption: isSeawater
        ? convertToNumber(userProjectedFeedConsumption)
        : undefined,
      userProjectedSales: parseFloat(userProjectedSales),
      userProjectedPosition,
    }

    if (setScrollHeight) {
      setScrollHeight()
    }

    const errors = await withPromise(saveSingleItemForm, {
      data,
      id,
      sucessMessage: formatMessage(messages.siteUpdatedSuccessfully),
      siteType,
      snapshot,
    }).then(() => {
      onPanelClose()
      fetchProjectedSitesManagerSingleItem({
        psfs: storePsfs,
        siteType,
        snapshot,
        siteId,
        specieId,
        year,
      })
    })

    if (errors) {
      return errors
    }

    await withPromise(fetchProjectedSitesManagerSingleItem, {
      psfs: storePsfs,
      siteType,
      snapshot,
      siteId,
      specieId,
      year,
    })

    scrollToCurrentProduct()
    onPanelClose()
  }

  const onChangeSelect = (value, field, dependency) => {
    if (field === 'userProjectedAdditives2' && value && value.length > 3) {
      setActiveSiteRow({ [field]: value.slice(0, 3) })

      return
    }

    if (activeSiteRow[field] !== value) {
      setActiveSiteRow({ [field]: value })

      if (dependency) {
        switch (dependency) {
          case 'dietGroup': {
            setActiveSiteRow({
              userProjectedDietGroup: null,
            })

            if (!value) {
              setDietGroups(List([]))
            } else {
              fetchProjectedSitesManagerDietGroups({
                productFamilyId: value,
              })
            }
            break
          }
          default:
        }
      }
    }

    return value
  }

  const renderClearButton = (field, dependency) => (
    <div
      className="clear-field"
      onClick={() => {
        onChangeSelect(null, field, dependency)
      }}
    />
  )

  return (
    <Form
      onSubmit={onSubmitForm}
      initialValues={activeSiteRow}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit} className="event-details-form">
          <div className="form-group">
            <Field
              name="userProjectedProductFamilyId"
              component={Select}
              label={formatMessage(messages.userProjectedProductFamily)}
              formControlProps={{
                fullWidth: true,
              }}
              useSearch
              items={productFamilyList}
              parse={value =>
                onChangeSelect(
                  value,
                  'userProjectedProductFamilyId',
                  'dietGroup',
                )
              }
            />
            {!!activeSiteRow.userProjectedProductFamilyId &&
              renderClearButton('userProjectedProductFamilyId', 'dietGroup')}
          </div>
          <div className="form-group">
            <Field
              name="userProjectedDietGroup"
              component={Select}
              label={formatMessage(messages.userProjectedDietGroup)}
              formControlProps={{
                fullWidth: true,
              }}
              parse={value => onChangeSelect(value, 'userProjectedDietGroup')}
              useSearch
              items={userProjectedDietGroupList}
              disabled={!userProjectedDietGroupList.length}
            />
            {!!activeSiteRow.userProjectedDietGroup &&
              renderClearButton('userProjectedDietGroup')}
          </div>
          <div className="form-group">
            <Field
              name="userProjectedHistidine"
              component={Select}
              label={formatMessage(messages.userProjectedHistidine)}
              formControlProps={{
                fullWidth: true,
              }}
              parse={value => onChangeSelect(value, 'userProjectedHistidine')}
              disabled={!histidineOptions.length}
              useSearch
              items={userProjectedHistidineSelectList}
            />
            {!!activeSiteRow.userProjectedHistidine &&
              renderClearButton('userProjectedHistidine')}
          </div>
          <div className="form-group">
            <Field
              name="userProjectedPigment2"
              component={Select}
              label={formatMessage(messages.userProjectedPigment)}
              formControlProps={{
                fullWidth: true,
              }}
              parse={value => onChangeSelect(value, 'userProjectedPigment2')}
              disabled={!pigmentOptions.length}
              useSearch
              items={userProjectedPigmentSelectList}
            />
            {!!activeSiteRow.userProjectedPigment2 &&
              renderClearButton('userProjectedPigment2')}
          </div>
          <div className="form-group">
            <Field
              multiple
              name="userProjectedAdditives2"
              component={Select}
              format={value =>
                value
                  ? value.filter(item =>
                      userProjectedAdditivesSelectList.some(
                        ({ id }) => id === item,
                      ),
                    )
                  : []
              }
              label={formatMessage(messages.userProjectedAdditives)}
              formControlProps={{
                fullWidth: true,
                multiple: true,
              }}
              parse={value => onChangeSelect(value, 'userProjectedAdditives2')}
              disabled={!userProjectedAdditivesSelectList.length}
              useSearch
              items={userProjectedAdditivesSelectList}
            />
            {activeSiteRow.userProjectedAdditives2 &&
              !!activeSiteRow.userProjectedAdditives2.length &&
              renderClearButton('userProjectedAdditives2')}
          </div>
          {isSeawater &&
            renderNumberInput('userProjectedFeedConsumption', false)}
          {renderNumberInput('userProjectedSales', false)}
          <div className="form-group">
            <Field
              multiple
              name="reason"
              component={Select}
              format={value => value || []}
              label={formatMessage(messages.reason)}
              formControlProps={{
                fullWidth: true,
                multiple: true,
              }}
              parse={value => onChangeSelect(value, 'reason')}
              useSearch
              items={itemReasons.map(reason => ({
                name: formatMessage({ id: reason }),
                id: reason,
              }))}
            />
            {!!activeSiteRow.reason &&
              !!activeSiteRow.reason.length &&
              renderClearButton('reason')}
          </div>
          <div className="form-button-group">
            <Button
              href={null}
              type="button"
              color="default"
              size="medium"
              variant="contained"
              onClick={onPanelClose}
              className="cancel-event-btn"
              fullWidth={fullWidth}>
              {formatMessage(messages.cancel)}
            </Button>
            <Button
              href={null}
              type="submit"
              color="primary"
              size="medium"
              variant="contained"
              onClick={handleSubmit}
              className="save-event-btn"
              fullWidth={fullWidth}>
              {formatMessage(messages.save)}
            </Button>
          </div>
        </form>
      )}
    />
  )
}

export default connect(
  state => ({
    activeSiteRow: state.getIn(['projectedSitesManager', 'activeSiteRow']),
    productFamilyList: state.getIn([
      'projectedSitesManager',
      'productFamilyList',
    ]),
    baseProductList: state.getIn(['projectedSitesManager', 'baseProductList']),
    dietGroupList: state.getIn(['projectedSitesManager', 'dietGroupList']),
    projectedProductList: state.getIn([
      'projectedSitesManager',
      'projectedProductList',
    ]),
    singleItemFormLookup: state.getIn([
      'projectedSitesManager',
      'singleItemFormLookup',
    ]),
    storePsfs: state.getIn(['projectedSitesManager', 'storePsfs']),
  }),
  {
    setActiveSiteRow,
    saveSingleItemForm,
    fetchProjectedSitesManagerProductFamily,
    fetchProjectedSitesManagerSingleItem,
    fetchProjectedSitesManagerDietGroups,
    setProjectedProducts,
    setBaseProducts,
    setDietGroups,
    fetchProjectedSitesManagerLookupTable709,
  },
)(injectIntl(EditForm))
